<template lang="pug">
FetchErrorTransition(:isFetching="isFetching" :error="error" :parsedException="parsedException")
  UpsertProductValidator(
    v-if="!isFetching && !error"
    ref="createEditProduct"
    :forEditorModel="forEditorModel"
    :disabled="isSaving"
    :isSaving="isSaving"
    :price-includes-tax-default-value="priceIncludesTaxDefaultValue"
    :enableProductDurationPerParticipant="enableProductDurationPerParticipant"
    :show-accounting-fields="showAccountingFields"
    :canViewAuditTrail="showAuditTrail"
    @save="save"
    @cancel="backToOverview()"
    :enableMultilanguage="enableMultilanguage"
    )
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import UpsertProductValidator from './UpsertProductValidator.vue'
import tryCall from '@v/mixins/tryCall.js'
import { map, clone, toLower } from 'ramda'
import { parseAxiosException } from '@/helpers/exceptionParser.js'
import ProductForEditorApiModel from './models/ProductForEditorApiModel.js'

export default {
  name: 'UpsertProductWrapper',
  components: { UpsertProductValidator },
  mixins: [tryCall],
  props: {
    productKey: String,
    priceIncludesTaxDefaultValue: Boolean,
    showAccountingFields: Boolean,
  },
  data() {
    // todo: refactor so that the foreditor model is passed on instead of all its contents individually
    return {
      isFetching: true,
      isSaving: false,
      error: null,
      parsedException: null,
      forEditorModel: null,
      featureProductDurationPerParticipant: 'ProductDurationPerParticipant',
    }
  },
  computed: {
    ...mapState('imagePicker', ['file', 'removeExistingFile']),
    ...mapState('inventory', ['products']),
    ...mapGetters('visitorClassifications', ['classificationCode', 'classification']),
    ...mapGetters('briqState', ['userCanViewAuditTrail', 'enabledFeatures', 'envFeatureFlags']),
    enableProductDurationPerParticipant() {
      return this.enabledFeatures?.includes(this.featureProductDurationPerParticipant)
    },
    enableMultilanguage() {
      return this.envFeatureFlags['enable-multilanguage']
    },
    showAuditTrail() {
      return this.productKey && this.userCanViewAuditTrail
    },
    segmentUrl() {
      return this.productKey && this.classificationCode
        ? `products/${this.productKey}/segments/${this.classificationCode}`
        : null
    },
  },
  methods: {
    ...mapActions('inventory', ['updateProduct']),
    ...mapActions('imagePicker', ['initStateData']),
    async save(product) {
      this.isSaving = true
      if (this.removeExistingFile) {
        // TODO - Remove the image by calling an endpoint, but that does not exist yet; apparently no images were ever deleted until now
        // A ticket has been created for this: https://briqbookings.atlassian.net/browse/APP-1470
        product.Image = null
      }
      const saveImageResult = await this.saveImage()
      product.Image = saveImageResult.data || product.Image
      const saveProductResult = await this.saveProduct(product)
      const success = saveImageResult.success && saveProductResult.success
      if (success) {
        this.$notifySuccess('Product saved')
        this.updateProduct(saveProductResult.product)
        this.backToOverview()
      }
      this.isSaving = false
    },
    async saveImage() {
      return this.tryCall(async () => {
        // todo: replace with action
        if (!this.file) return
        const formData = new FormData()
        formData.append('image', this.file)
        const response = await this.$apiClient.post(`//${window.location.host}/image`, formData, {
          headers: {
            accept: 'application/json',
            'Accept-Language': 'en-US,en;q=0.8',
            'Content-Type': `multipart/form-data; boundary=${formData._boundary}`,
          },
        })
        return response.data.url
      }, 'Could not save image!')
    },
    // obsolete?
    sanitizeActiveSegments(product) {
      // ensure that classification and segment codes are all saved in lowercase
      const cloned = clone(product)
      const entries = Object.entries(cloned.ActiveSegments)
      const sanitized = map(([k, v]) => [toLower(k), v.map(toLower)])(entries)
      cloned.ActiveSegments = Object.fromEntries(sanitized)
      return cloned
    },
    async saveProduct(saveModel) {
      try {
        const response = await this.$apiClient.post('products/save', saveModel)
        return { success: true, product: response.data }
      } catch (e) {
        const parsedException = parseAxiosException(e)
        this.$logError(`Error expected to originate from ${parsedException.origin}`)
        this.$logError(parsedException.data)
        this.$notifyError('Something went wrong')
        return { success: false, error: e }
      }
    },
    backToOverview() {
      this.$router.push({ name: 'Products' })
    },
  },
  async beforeMount() {
    this.isFetching = true
    const urlAppendix = this.productKey ? `for-edit/${this.productKey}` : 'for-create'
    const url = 'products/' + urlAppendix
    try {
      const result = await this.$apiClient.get(url)
      this.forEditorModel = new ProductForEditorApiModel(result.data)
      if (this.productKey)
        this.$setDocumentTitle(`Products | ${this.forEditorModel.Product.NameInternal}`)
    } catch (e) {
      this.error = e
      const parsedException = parseAxiosException(e)
      this.parsedException = parsedException
      this.$logError(parsedException)
      this.$notifyError('Something went wrong')
    }
    this.isFetching = false
    this.initStateData()
  },
}
</script>

<style scoped lang="stylus">
.loading
  position absolute
  top 50%
  transform translateY(-50%)
</style>
